import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import axios from "axios";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useShop, API } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../mock";
import { PageTitle } from "../components/Shared";
import Seo from "../components/Seo";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  zip: "",
  country: "Italia",
};

const consentTexts = {
  it: {
    termsPre: "Ho letto e accetto i ",
    termsLink: "Termini e condizioni",
    waiver:
      "Confermo di aver letto e accetto che, richiedendo l'accesso immediato al download del prodotto digitale, perdo il diritto di recesso di 14 giorni ai sensi dell'art. 59 del D.Lgs. 206/2005",
    consentRequired: "Devi accettare le condizioni per procedere",
  },
  en: {
    termsPre: "I have read and accept the ",
    termsLink: "Terms and Conditions",
    waiver:
      "I confirm that I have read and accept that, by requesting immediate access to the digital product download, I lose the 14-day right of withdrawal pursuant to art. 59 of Italian Legislative Decree 206/2005",
    consentRequired: "You must accept the conditions to proceed",
  },
};

const CheckoutPage = () => {
  const { t, lang, cart, cartTotal, products } = useShop();
  const { lp } = useLocale();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!user) return;
    const parts = (user.name || "").trim().split(/\s+/);
    const firstName = parts[0] || "";
    const lastName = parts.slice(1).join(" ") || "";
    setForm((prev) => ({
      ...prev,
      firstName: prev.firstName || firstName,
      lastName: prev.lastName || lastName,
      email: prev.email || user.email || "",
      phone: prev.phone || user.phone || "",
    }));
  }, [user]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptWaiver, setAcceptWaiver] = useState(false);
  const [consentErrors, setConsentErrors] = useState({});

  if (cart.length === 0) return <Navigate to={lp("/cart")} replace />;

  const ct = consentTexts[lang];
  const hasDownloadable = cart.some((i) => {
    const p = products.find((x) => x.id === i.id);
    return p && (p.is_digital || p.digital_file);
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["firstName", "lastName", "email", "address", "city", "zip"];
    const newErrors = {};
    required.forEach((f) => {
      if (!form[f].trim()) newErrors[f] = true;
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = true;
    }
    const newConsentErrors = {};
    if (!acceptTerms) newConsentErrors.terms = true;
    if (hasDownloadable && !acceptWaiver) newConsentErrors.waiver = true;
    if (Object.keys(newErrors).length > 0 || Object.keys(newConsentErrors).length > 0) {
      setErrors(newErrors);
      setConsentErrors(newConsentErrors);
      if (Object.keys(newConsentErrors).length > 0) toast.error(ct.consentRequired);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        customer: form,
        origin_url: window.location.origin,
        lang,
        accepted_terms: acceptTerms,
        accepted_waiver: hasDownloadable ? acceptWaiver : null,
      };
      const res = await axios.post(`${API}/checkout/session`, payload);
      if (res.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      console.error("Checkout error", err);
      toast.error(
        lang === "it"
          ? "Errore durante la creazione del pagamento. Riprova."
          : "Error creating the payment session. Please try again."
      );
      setSubmitting(false);
    }
  };

  const fields = [
    { name: "firstName", label: t("firstName"), required: true, half: true },
    { name: "lastName", label: t("lastName"), required: true, half: true },
    { name: "email", label: t("email"), required: true, type: "email" },
    { name: "phone", label: t("phone") },
    { name: "address", label: t("address"), required: true },
    { name: "city", label: t("city"), required: true, half: true },
    { name: "zip", label: t("zip"), required: true, half: true },
    { name: "country", label: t("country") },
  ];

  return (
    <div data-testid="checkout-page">
      <Seo title={t("checkout")} />
      <PageTitle title={t("checkout")} />
      <div className="idm-container idm-checkout-layout">
        <form className="idm-billing" onSubmit={handleSubmit} noValidate>
          <h3>{t("billingDetails")}</h3>
          <div className="idm-form-grid">
            {fields.map((f) => (
              <div key={f.name} className={`idm-field ${f.half ? "half" : ""}`}>
                <label htmlFor={f.name}>
                  {f.label} {f.required && <span className="idm-req">*</span>}
                </label>
                <input
                  id={f.name}
                  name={f.name}
                  type={f.type || "text"}
                  value={form[f.name]}
                  onChange={handleChange}
                  className={errors[f.name] ? "error" : ""}
                  data-testid={`checkout-${f.name}`}
                />
                {errors[f.name] && (
                  <span className="idm-field-error">{t("requiredField")}</span>
                )}
              </div>
            ))}
          </div>
        </form>

        <div className="idm-order-summary" data-testid="order-summary">
          <h3>{t("yourOrder")}</h3>
          <div className="idm-summary-rows">
            {cart.map((item) => (
              <div className="idm-totals-row" key={item.id}>
                <span>
                  {item.name[lang]} × {item.qty}
                </span>
                <span>{formatPrice(item.price * item.qty)}</span>
              </div>
            ))}
            <div className="idm-totals-row idm-totals-total">
              <span>{t("total")}</span>
              <span data-testid="checkout-total">{formatPrice(cartTotal)}</span>
            </div>
          </div>

          <div className="idm-consents" data-testid="checkout-consents">
            <label
              className={`idm-consent ${consentErrors.terms ? "error" : ""}`}
              data-testid="consent-terms"
            >
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  setConsentErrors({ ...consentErrors, terms: false });
                }}
                data-testid="consent-terms-checkbox"
              />
              <span>
                {ct.termsPre}
                <Link to={lp("/pagine/termini-e-condizioni")} target="_blank" rel="noreferrer">
                  {ct.termsLink}
                </Link>{" "}
                <span className="idm-req">*</span>
              </span>
            </label>
            {hasDownloadable && (
              <label
                className={`idm-consent ${consentErrors.waiver ? "error" : ""}`}
                data-testid="consent-waiver"
              >
                <input
                  type="checkbox"
                  checked={acceptWaiver}
                  onChange={(e) => {
                    setAcceptWaiver(e.target.checked);
                    setConsentErrors({ ...consentErrors, waiver: false });
                  }}
                  data-testid="consent-waiver-checkbox"
                />
                <span>
                  {ct.waiver} <span className="idm-req">*</span>
                </span>
              </label>
            )}
          </div>

          <p className="idm-payment-note">
            <Lock size={13} /> {t("secureCheckout")} — Stripe
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="idm-btn-yellow idm-btn-block"
            data-testid="place-order-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="idm-spin" />
                {lang === "it" ? "Reindirizzamento..." : "Redirecting..."}
              </>
            ) : (
              t("placeOrder")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
